import { Box, Grid, Button, Typography, Collapse } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";
import { api, TaskDefinitionDTO } from "../../logic/api";
import { SessionContext } from "../../utility/useSession";
import { UATSnapshotButton } from "./UATSnapshotButton";
import { UATSnapshotStatus } from "./UATSnapshotStatus";


export interface UATEnvironmentSectionProps {
  definition: TaskDefinitionDTO;
}

export const UATEnvironmentSection = ({ definition }: UATEnvironmentSectionProps) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const [instanceState, setInstanceState] = useState<Record<string, unknown>>({});
  const [showProgress, setShowProgress] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const submitStartTaskCommandApi = api.hooks.task.submitStartTaskCommand();
  const getDefinitionApi = api.hooks.task.getDefinition();

  const isInProgress = ['RUNNING'].includes(instanceState.status as string)
  const isButtonDisabled = isInProgress || isInitialLoading;

  const refreshInstance = (isPolling = false) => {
    if (!definition.uuid) {
      return;
    }

    // set loading state if it's the initial load
    if (!isPolling) {
      setIsInitialLoading(true);
    }

    getDefinitionApi.send(
      session.current.context,
      { uuid: definition.uuid },
      (data) => {
        if (data?.state) {
          setInstanceState(data.state ?? {});
        }
        setIsInitialLoading(false);
      }
    );
  }

  useEffect(refreshInstance, []);

  useEffect(() => {
    if (!isInProgress) {
      return;
    }

    const interval = setInterval(
      () => refreshInstance(true),
      5000
    );

    return () => clearInterval(interval);
  }, [isInProgress]);

  const handleCreateSnapshot = async () => {
    try {
      setError(null);
      setInstanceState({});

      submitStartTaskCommandApi.send(
        session.current.context,
        {
          taskDefinitionUuid: definition.uuid,
        },
        () => {
          setInstanceState({ status: 'RUNNING' }); // optimistic running
          setShowProgress(true);
        }
      );
    } catch (error) {
      setError("Failed to create snapshot. Please check that the tenant exists in the target environment and is properly configured.");
      setInstanceState({});
    }
  };

  const handleShowProgress = () => {
    setShowProgress((prev) => !prev);
  }

  return (
    <NotchedCard
      style={{ marginTop: 16 }}
      label={t("taskRunner.copyTenantData.title")}
    >
      <Grid container columns={1}>
        <Grid size={1} mb={1}>
          <Typography variant="body2" gutterBottom>
            {t("taskRunner.copyTenantData.description")}
          </Typography>
        </Grid>

        <Grid size={1} container columns={2} spacing={1}>
          <UATSnapshotButton
            disabled={isButtonDisabled}
            onCreateSnapshot={handleCreateSnapshot}
            isInProgress={isInProgress}
            isLoading={isInitialLoading}
            taskUuid={definition.uuid}
          />
          {Array.isArray(instanceState.steps) && (
            <Button
              onClick={handleShowProgress}
              variant="text"
              data-testid={`show-hide-progress-button-${definition.uuid}`}
              sx={{
                transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                "&:active": {
                  transform: "scale(0.99)",
                },
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
                "&:focus-visible": {
                  boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                },
              }}
              disableRipple
              disableElevation
            >
              {showProgress
                ? t("taskRunner.progress.hide")
                : t("taskRunner.progress.show")
              }
            </Button>
          )}
        </Grid>

        <Grid size={1}>
          {error && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "error.light", borderRadius: 1, border: "1px solid", borderColor: "error.main" }}>
              <Typography variant="body2" color="error.dark">
                {error}
              </Typography>
            </Box>
          )}
          <Collapse in={Boolean(Array.isArray(instanceState.steps) && showProgress)} timeout="auto" data-testid={`progress-collapse-${definition.uuid}`}>
            <UATSnapshotStatus state={instanceState} />
          </Collapse>
        </Grid>
      </Grid>
    </NotchedCard>
  );
};
